// Reports Admin Panel — v190.6.
// Shows the 5 role-based report templates with LIVE Meta status,
// last report_runs, last report_incidents, and lets the admin fire
// on-demand runs / scans (with mock template on Preview).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import ReportRecipientsPanel from "./ReportRecipientsPanel";

const REPORT_NAMES = [
  "daily_ceo_report_ar_v1",
  "daily_marketing_report_ar_v1",
  "daily_operations_report_ar_v1",
  "incident_alert_ar_v1",
  "incident_recovery_ar_v1",
];

const STATUS_STYLES = {
  APPROVED: { label: "معتمد", bg: "#dcfce7", fg: "#15803D" },
  PENDING: { label: "بانتظار الاعتماد", bg: "#fef3c7", fg: "#B45309" },
  REJECTED: { label: "مرفوض", bg: "#fee2e2", fg: "#B91C1C" },
  NOT_FOUND: { label: "لم يُرفع بعد", bg: "#f1f5f9", fg: "#475569" },
};

export default function ReportsAdminPanel() {
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [defs, setDefs] = useState([]);
  const [activations, setActivations] = useState([]);
  const [runs, setRuns] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [waEnabled, setWaEnabled] = useState(null);

  const load = useCallback(async () => {
    try {
      const [wa, r, actR, runsR, incR, enR] = await Promise.all([
        api.get("/whatsapp/templates"),
        api.get("/reports/templates"),
        api.get("/reports/activations"),
        api.get("/reports/runs?limit=15"),
        api.get("/reports/incidents?limit=15"),
        api.get("/reports/whatsapp-enabled"),
      ]);
      setMetaTemplates(wa.data?.items || []);
      setDefs(r.data?.templates || []);
      setActivations(actR.data?.activations || []);
      setRuns(runsR.data?.runs || []);
      setIncidents(incR.data?.incidents || []);
      setWaEnabled(!!enR.data?.enabled);
    } catch (e) {
      toast.error("تعذّر تحميل بيانات محرك التقارير");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const templateStatusByName = useMemo(() => {
    const m = {};
    (metaTemplates || []).forEach((t) => {
      if (REPORT_NAMES.includes(t.name)) {
        m[t.name] = t.status || "PENDING";
      }
    });
    return m;
  }, [metaTemplates]);

  const activationByName = useMemo(() => {
    const m = {};
    (activations || []).forEach((a) => { m[a.template_name] = a; });
    return m;
  }, [activations]);

  const activateOne = async (name) => {
    const note = window.prompt(
      `تفعيل «${name}» للإرسال الفعلي.\n\nيُشترط أنك رأيت أول رسالة تجريبية على جوّالك.\nأدخل ملاحظة قصيرة (اختيارية):`,
      "",
    );
    if (note === null) return;
    setBusy(true);
    try {
      await api.post("/reports/activate", { template_name: name, note });
      toast.success(`تم تفعيل «${name}» — أصبح live send مسموحًا`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "فشل التفعيل";
      toast.error(typeof msg === "string" ? msg : "فشل التفعيل");
    } finally { setBusy(false); }
  };

  const deactivateOne = async (name) => {
    const reason = window.prompt(`إيقاف live send لقالب «${name}» — سبب الإيقاف؟`, "");
    if (reason === null) return;
    setBusy(true);
    try {
      await api.post("/reports/deactivate", { template_name: name, reason });
      toast.success(`تم الإيقاف — «${name}» رجع إلى mock`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإيقاف");
    } finally { setBusy(false); }
  };

  const syncMeta = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reports/sync-meta-status", {});
      const trans = r.data?.approved_transitions || [];
      if (trans.length) {
        toast.success(`اعتُمد على Meta: ${trans.join(", ")} — راجع اللوحة لتفعيلها يدويًا`);
      } else {
        toast.success(`تمت المزامنة — لا اعتمادات جديدة (${r.data?.updates || 0} تحديث)`);
      }
      await load();
    } catch (e) {
      toast.error("فشل مزامنة حالة Meta");
    } finally { setBusy(false); }
  };

  const verifyToken = async () => {
    setBusy(true);
    setVerifyResult(null);
    try {
      const r = await api.post("/reports/templates/verify-token", {});
      setVerifyResult({ ok: true, ...r.data });
      toast.success(`التوكن سليم — ${r.data?.template_count || 0} قالب مقروء`);
    } catch (e) {
      const d = e?.response?.data?.detail || e?.response?.data;
      setVerifyResult({ ok: false, ...(d || {}) });
      toast.error("فشل التحقق من التوكن — راجع التفاصيل بالأسفل");
    } finally {
      setBusy(false);
    }
  };

  const uploadTemplates = async () => {
    if (!window.confirm("سيتم رفع القوالب الخمسة إلى Meta الآن (UTILITY). متابعة؟")) return;
    setBusy(true);
    try {
      const r = await api.post("/reports/templates/upload", {});
      toast.success(`تم — مرفوع: ${r.data?.counts?.submitted || 0} • موجود مسبقاً: ${r.data?.counts?.already_exists || 0} • مرفوض: ${r.data?.counts?.rejected || 0}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الرفع");
    } finally {
      setBusy(false);
    }
  };

  const runBundle = async (mock) => {
    setBusy(true);
    try {
      const r = await api.post("/reports/run-now", {
        bundle: true, dry_run: !mock, use_mock_template: !!mock,
      });
      const okCount = (r.data?.runs || []).filter((x) => x.status === "sent" || x.status === "dry_run").length;
      toast.success(`تم — ${okCount}/${(r.data?.runs || []).length} تقارير`);
      await load();
    } catch (e) {
      toast.error("فشل تشغيل الحزمة");
    } finally {
      setBusy(false);
    }
  };

  const scanNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reports/scan-now?use_mock_template=true", {});
      toast.success(`فحص الحوادث — فتح: ${r.data?.integration?.opened || 0} + ${r.data?.cash?.opened || 0} • إغلاق: ${r.data?.integration?.resolved || 0}`);
      await load();
    } catch (e) {
      toast.error("فشل الفحص");
    } finally {
      setBusy(false);
    }
  };

  const toggleWhatsAppEnabled = async () => {
    const next = !waEnabled;
    if (!window.confirm(next
      ? "تفعيل إرسال التقارير عبر واتساب فعلياً؟ (يشترط أن يكون القالب معتمداً ومُفعَّلاً أيضاً)"
      : "إيقاف أي إرسال واتساب لتقارير الأدوار فوراً؟")) return;
    setBusy(true);
    try {
      const r = await api.put("/reports/whatsapp-enabled", { enabled: next });
      setWaEnabled(!!r.data?.enabled);
      toast.success(next ? "✅ إرسال واتساب للتقارير مفعَّل" : "🛑 إرسال واتساب للتقارير مُوقَف");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تغيير المفتاح");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="reports-admin-panel" style={{ direction: "rtl", padding: 16, background: "#FBF8F3", borderRadius: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: "#7C2D12" }} data-testid="reports-title">
            📊 محرك التقارير المُوحّد بالأدوار
          </h2>
          <div style={{ color: "#78716C", fontSize: 13, marginTop: 4 }}>
            5 قوالب UTILITY • تقارير يومية 07:00 KSA • تنبيهات فورية عبر watcher الحوادث
          </div>
          <div style={{ color: "#7C2D12", background: "#FEF3C7", borderRadius: 8,
                         padding: "6px 10px", fontSize: 12, marginTop: 6,
                         maxWidth: 700, lineHeight: 1.55 }} data-testid="reports-owner-gate-notice">
            🛡 <b>بوابة المالك:</b> اعتماد Meta وحده لا يفتح الإرسال الفعلي.
            كل قالب يبقى محجوباً (رفض صريح، بلا قالب بديل) حتى تفعِّله يدويًا من
            هذه اللوحة بعد رؤية أول رسالة تجريبية على جوّالك. الوكيل لا يستطيع
            التفعيل نيابةً عنك.
          </div>
          <div data-testid="reports-whatsapp-enabled-notice" style={{
                 color: waEnabled ? "#166534" : "#991B1B",
                 background: waEnabled ? "#DCFCE7" : "#FEE2E2", borderRadius: 8,
                 padding: "6px 10px", fontSize: 12, marginTop: 6, maxWidth: 700,
                 display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span>
              {waEnabled === null ? "⏳ جارٍ تحميل حالة المفتاح…"
                : waEnabled ? "🟢 إرسال واتساب لتقارير الأدوار: مفعَّل"
                : "🔴 إرسال واتساب لتقارير الأدوار: مُوقَف — التقارير تُبنى وتُحفظ فقط"}
            </span>
            {waEnabled !== null && (
              <button data-testid="reports-whatsapp-enabled-toggle" onClick={toggleWhatsAppEnabled}
                      disabled={busy} style={{ ...btn(waEnabled ? "#FEE2E2" : "#DCFCE7",
                      waEnabled ? "#B91C1C" : "#166534"), padding: "4px 10px", fontSize: 12 }}>
                {waEnabled ? "إيقاف" : "تفعيل"}
              </button>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button data-testid="reports-sync-meta" onClick={syncMeta} disabled={busy}
                   style={btn("#FEF3C7", "#B45309")}>🔄 مزامنة حالة Meta</button>
          <button data-testid="reports-verify-token" onClick={verifyToken} disabled={busy}
                   style={btn("#F5F5F4", "#44403C")}>🔑 تحقّق من التوكن</button>
          <button data-testid="reports-upload-templates" onClick={uploadTemplates} disabled={busy}
                   style={btn("#FEE2C7", "#7C2D12")}>⬆️ ارفع القوالب إلى Meta</button>
          <button data-testid="reports-scan-now" onClick={scanNow} disabled={busy}
                   style={btn("#DBEAFE", "#1E40AF")}>🔍 فحص الحوادث الآن</button>
          <button data-testid="reports-run-bundle" onClick={() => runBundle(false)} disabled={busy}
                   style={btn("#DCFCE7", "#166534")}>🧪 حزمة يومية — Dry Run</button>
          <button data-testid="reports-run-bundle-mock" onClick={() => runBundle(true)} disabled={busy}
                   style={btn("#E9D5FF", "#6B21A8")}>🎭 حزمة يومية — Mock إلى WA</button>
        </div>
      </div>

      {verifyResult && (
        <div data-testid="reports-verify-result" style={{
          padding: 12, marginBottom: 12, borderRadius: 12,
          background: verifyResult.ok ? "#DCFCE7" : "#FEE2E2",
          color: verifyResult.ok ? "#166534" : "#7F1D1D",
          fontSize: 13,
        }}>
          {verifyResult.ok
            ? `✅ ${verifyResult.message || "التوكن يعمل"} — عدد القوالب المقروءة: ${verifyResult.template_count || 0}`
            : `❌ ${verifyResult.message || "فشل الفحص"} — تحقق من صلاحيات التوكن`}
        </div>
      )}

      {/* ── 5 templates grid ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12, marginBottom: 24 }}
           data-testid="reports-templates-grid">
        {defs.map((t) => {
          const st = templateStatusByName[t.name] || "NOT_FOUND";
          const cfg = STATUS_STYLES[st] || STATUS_STYLES.PENDING;
          const act = activationByName[t.name];
          const isActivated = !!act?.activated_by_owner;
          const canActivate = st === "APPROVED" && !isActivated;
          return (
            <div key={t.name} data-testid={`report-tpl-card-${t.name}`}
                 style={{ background: "#fff", borderRadius: 14,
                           border: isActivated ? "2px solid #15803D" : "1px solid #F5F5F4",
                           padding: 14, boxShadow: "0 1px 2px rgba(0,0,0,0.03)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <code style={{ fontSize: 12, color: "#374151" }} data-testid={`report-tpl-name-${t.name}`}>{t.name}</code>
                <span data-testid={`report-tpl-status-${t.name}`}
                      style={{ background: cfg.bg, color: cfg.fg, padding: "2px 10px",
                                borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{cfg.label}</span>
              </div>
              <div style={{ color: "#7C2D12", fontSize: 12, marginBottom: 6 }}>
                {t.category} • {t.language} • header={t.header ? "✓" : "—"} • footer={t.footer ? "✓" : "—"}
              </div>
              <div style={{ color: "#57534E", fontSize: 12, marginBottom: 6 }}>
                📝 header: <span style={{ color: "#292524" }}>{t.header || "—"}</span>
              </div>
              <div style={{ color: "#57534E", fontSize: 12, marginBottom: 6 }}>
                🔢 body variables: <b>{(t.sample_values || []).length}</b>
              </div>
              <div style={{ color: "#57534E", fontSize: 12, marginBottom: 10 }}>
                🔻 footer: <span style={{ color: "#292524" }}>{t.footer || "—"}</span>
              </div>

              {/* ── Owner activation gate ── */}
              <div data-testid={`report-tpl-activation-${t.name}`}
                   style={{ background: isActivated ? "#DCFCE7" : "#FEF3C7",
                             borderRadius: 10, padding: "8px 10px", fontSize: 12,
                             color: isActivated ? "#166534" : "#78350F", marginBottom: 6 }}>
                {isActivated ? (
                  <>
                    ✅ <b>مُفعَّل للإرسال الفعلي</b>
                    <div style={{ opacity: 0.8, marginTop: 2 }}>
                      بواسطة: {act?.activated_by || "—"} • {(act?.activated_at || "").slice(0, 16).replace("T", " ")}
                    </div>
                    {act?.activation_note && <div style={{ opacity: 0.75, marginTop: 2 }}>📝 {act.activation_note}</div>}
                  </>
                ) : st === "APPROVED" ? (
                  <>
                    🛎 <b>APPROVED — بانتظار تفعيلك اليدوي</b>
                    <div style={{ opacity: 0.8, marginTop: 2 }}>
                      اعتمدت Meta هذا القالب. الإرسال الفعلي يبقى mock حتى تفعِّله بنفسك بعد رؤية أول رسالة تجريبية.
                    </div>
                  </>
                ) : (
                  <>
                    ⏳ <b>يبقى mock — Meta لم يعتمده بعد</b>
                    <div style={{ opacity: 0.8, marginTop: 2 }}>
                      الحالة الحالية: {cfg.label}. لا يمكن التفعيل حتى تعتمده Meta.
                    </div>
                  </>
                )}
              </div>

              <div style={{ display: "flex", gap: 6 }}>
                {canActivate && (
                  <button data-testid={`report-tpl-activate-${t.name}`}
                           onClick={() => activateOne(t.name)} disabled={busy}
                           style={{ ...btn("#DCFCE7", "#166534"), padding: "6px 10px", fontSize: 12, flex: 1 }}>
                    ✅ فعّل الإرسال الفعلي (CEO)
                  </button>
                )}
                {isActivated && (
                  <button data-testid={`report-tpl-deactivate-${t.name}`}
                           onClick={() => deactivateOne(t.name)} disabled={busy}
                           style={{ ...btn("#FEE2E2", "#B91C1C"), padding: "6px 10px", fontSize: 12, flex: 1 }}>
                    🛑 أوقِف — رجوع إلى mock
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Per-report recipients whitelist ── */}
      <ReportRecipientsPanel />

      {/* ── Latest runs ── */}
      <h3 style={{ margin: "16px 0 8px", color: "#7C2D12", fontSize: 15 }}>🗂 آخر تشغيلات (report_runs)</h3>
      <div data-testid="reports-runs-table" style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #F5F5F4" }}>
        {runs.length === 0 ? (
          <div style={{ padding: 12, color: "#78716C", fontSize: 13 }}>لا توجد تشغيلات بعد.</div>
        ) : runs.map((r, i) => (
          <div key={r.id || i} data-testid={`report-run-row-${i}`}
               style={{ display: "grid", gridTemplateColumns: "1.4fr 0.7fr 1fr 1fr 0.6fr", gap: 8, padding: "10px 12px",
                         borderTop: i ? "1px solid #F5F5F4" : "none", fontSize: 13 }}>
            <code style={{ color: "#374151" }}>{r.template_name}</code>
            <span style={{ color: r.status === "sent" ? "#166534" : r.status === "failed" ? "#B91C1C" : "#78716C" }}>{r.status}</span>
            <span style={{ color: "#57534E" }}>{(r.created_at || "").slice(0, 19).replace("T", " ")}</span>
            <span style={{ color: "#57534E" }}>🎯 recipients: {(r.recipients || []).length}</span>
            <span style={{ color: "#78716C" }}>{r.dry_run ? "dry" : "live"}</span>
          </div>
        ))}
      </div>

      {/* ── Latest incidents ── */}
      <h3 style={{ margin: "16px 0 8px", color: "#7C2D12", fontSize: 15 }}>🚨 آخر الحوادث (report_incidents)</h3>
      <div data-testid="reports-incidents-table" style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #F5F5F4" }}>
        {incidents.length === 0 ? (
          <div style={{ padding: 12, color: "#78716C", fontSize: 13 }}>لا توجد حوادث مسجّلة.</div>
        ) : incidents.map((i, idx) => (
          <div key={i.id || idx} data-testid={`report-incident-row-${idx}`}
               style={{ display: "grid", gridTemplateColumns: "1fr 1fr 0.6fr 1fr 1fr", gap: 8, padding: "10px 12px",
                         borderTop: idx ? "1px solid #F5F5F4" : "none", fontSize: 13 }}>
            <span style={{ color: "#374151" }}>{i.type}</span>
            <span style={{ color: "#57534E" }}>{i.service || i.branch || "—"}</span>
            <span style={{ color: i.severity === "high" ? "#B91C1C" : "#B45309" }}>{i.severity}</span>
            <span style={{ color: "#57534E" }}>{(i.detected_at || "").slice(0, 19).replace("T", " ")}</span>
            <span style={{ color: i.resolved_at ? "#166534" : "#B45309" }}>
              {i.resolved_at ? `مُغلَق (${i.resolved_duration_min || 0}د)` : "مفتوح"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function btn(bg, fg) {
  return {
    background: bg, color: fg, border: "1px solid transparent",
    borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 600,
    cursor: "pointer",
  };
}
